<template>
    <div id="map"></div>
</template>

<script>
    import {mapState, mapGetters} from 'vuex'
    import L from 'leaflet'
    import 'leaflet.markercluster/dist/leaflet.markercluster.js'

    const COLORS = {
        emerald: {name: 'Emerald', color: '#2ECC71', opacity: .8, argb: '#CC2ecc71'},
        flower: {name: 'Flower', color: '#F1C40F', opacity: .8, argb: '#CCf1c40f'},
        wisteria: {name: 'Wisteria', color: '#8E44AD', opacity: .8, argb: '#CC8e44ad'},
        carrot: {name: 'Carrot', color: '#E67E22', opacity: .8, argb: '#CCe67e22'},
        alizarin: {name: 'Alizarin', color: '#E74C3C', opacity: .8, argb: '#CCe74c3c'},
        asphalt: {name: 'Asphalt', color: '#34495E', opacity: .45, argb: '#7334495e'},
        concrete: {name: 'Concrete', color: '#95A5A6', opacity: .8, argb: '#CC95a5a6'},
        river: {name: 'River', color: '#5AACE2', opacity: .8, argb: '#CC5AACE2'},
        green: {name: 'Greensea', color: '#16A085', opacity: .8, argb: '#CC16a085'},
        clouds: {name: 'Clouds', color: '#ECF0F1', opacity: .8, argb: '#CCecf0f1'},
        transparent: {name: 'Transparent', color: '#FFFFFF', opacity: 0, argb: '#00FFFFFF'}
    };

    export default {
        data () {
            return {
                map: null,
                clientMarker: null,
                clientMarkerOutline: null,
                pointMarkers: {},
                animationTime: 0
            };
        },
        computed: {
            ...mapState({
                outOfTime: state => state.teams.outOfTime,
                clientLatLng: state => state.client.latLng,
                clientLatLngAccuracy: state => {
                    if (!state.client.latLngAccuracy) {
                        return 20;
                    }

                    if (state.client.latLngAccuracy < 10) {
                        return 10;
                    }

                    if (state.client.latLngAccuracy > 30) {
                        return 30;
                    }

                    return state.client.latLngAccuracy;
                }
            }),
            ...mapGetters([
                'event',
                'team',
                'visitedQuestions'
            ])
        },
        mounted () {
            // Create Leaflet map
            this.map = L.map('map', {
                center: [0, 0],
                zoom: 0
            })

            // Use OSM tiles
            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
            }).addTo(this.map)

            // Testing
            window.enableMapDev = () => {
                this.map.on('click', e => {
                    this.$store.dispatch('setClientPosition', {
                        coords: {
                            latitude: e.latlng.lat,
                            longitude: e.latlng.lng,
                            accuracy: this.$store.state.client.latLngAccuracy
                        }
                    })
                })
            }

            if (NODE_ENV === 'development') {
                window.enableMapDev()
            }

            // Menu control
            this.map.addControl(new (L.Control.extend({
                options: {
                    position: 'topright'
                },
                onAdd: (map) => {
                    const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-control-custom menu-button');

                    container.innerHTML = '<i class="fa fa-bars"></i>';

                    container.addEventListener('click', e => {
                        e.stopPropagation();
                        this.$store.commit('OPEN_MENU');
                    });

                    return container;
                }
            })));

            // Timer
            this.map.addControl(new (L.Control.extend({
                options: {
                    position: 'topright'
                },
                onAdd: (map) => {
                    const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-control-custom timer');
                    container.id = 'timer';
                    container.style.display = 'none';
                    return container;
                }
            })));

            // Marker cluster group
            this.markerGroup = L.markerClusterGroup({
                spiderfyOnMaxZoom: false,
                zoomToBoundsOnClick: false,
                removeOutsideVisibleBounds: true,
                disableClusteringAtZoom: 18
            })

            this.markerGroup.addTo(this.map)

            this.map.on('layeradd', e => {
                if (e.layer._scoove) {
                    this.map.addLayer(e.layer._scoove.circle)
                }
            })

            this.map.on('layerremove', e => {
                if (e.layer._scoove) {
                    this.map.removeLayer(e.layer._scoove.circle)
                }
            })

            /*
             // Fullscreen control
             if (document.documentElement.requestFullscreen) {
             this.map.addControl(new (L.Control.extend({
             options: {
             position: 'topleft'
             //control position - allowed: 'topleft', 'topright', 'bottomleft', 'bottomright'
             },
             onAdd: (map) => {
             const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-control-custom');

             container.style.backgroundColor = 'white';
             container.style.width = '30px';
             container.style.height = '30px';

             container.addEventListener('click', e => {
             e.stopPropagation();

             if (!document.fullscreenElement) {
             document.documentElement.requestFullscreen();
             } else {
             document.exitFullscreen();
             }
             });

             return container;
             }
             })));
             }
             */

            // Initialize markers
            this.updateClientMarker();
            this.updatePointMarkers();

            // Animate client marker outline
            this.animationInterval = setInterval(() => this.animateClientMarker(), 16);
        },
        destroyed () {
            clearInterval(this.animationInterval);
        },
        methods: {
            animateClientMarker() {
                if (this.clientMarkerOutline) {
                    this.animationTime += 0.075;
                    this.clientMarkerOutline.setRadius(this.clientLatLngAccuracy * 3 / 2 + this.clientLatLngAccuracy / 2 * Math.sin(this.animationTime));
                }
            },
            updateClientMarker() {
                // Delete client marker if location unknown
                if (!this.clientLatLng) {
                    if (this.clientMarker) {
                        this.map.removeLayer(this.clientMarker);
                    }

                    return;
                }

                // Create marker if not initialized, otherwise update position
                if (!this.clientMarker) {
                    this.clientMarker = L.circle(this.clientLatLng, this.clientLatLngAccuracy, {
                        weight: 0,
                        fillOpacity: 0.5
                    }).addTo(this.map);

                    this.clientMarkerOutline = L.circle(this.clientLatLng, this.clientLatLngAccuracy, {
                        weight: 2,
                        fillOpacity: 0.2
                    }).addTo(this.map);
                } else {
                    this.clientMarker.setLatLng(this.clientLatLng);
                    this.clientMarker.setRadius(this.clientLatLngAccuracy);
                    this.clientMarkerOutline.setLatLng(this.clientLatLng);
                }
            },
            createPointMarker(point, pin, makeCircle=true) {
                const location = point.question.locationSpecific ? point.question.location : point.location;
                const latLng = L.latLng(location.latitude, location.longitude);
                
                // Marker
                const marker = L.marker(latLng, {
                    icon: L.icon(PIN_ICON)
                }).addTo(this.markerGroup);

                marker.bindTooltip(point.question.difficulty + '', {
                    permanent: true,
                    className: 'badge badge-bordered badge-index'
                });

                // Circle
                //const fill = COLORS[point.question.pin ? point.question.pin.areaColor : 'green'];

                const circleOptions = {
                    weight: 0,
                    fillColor: '#666666',
                    fillOpacity: 0.6
                    //fillColor: fill.color,
                    //fillOpacity: fill.opacity
                };

                const circle = L.circle(latLng, location.radius, circleOptions);

                // Marker options
                marker._scoove = { point, circle }

                /*
                if (makeCircle) {
                    circle.addTo(this.map);
                }
                */

                this.pointMarkers[point.question._id] = {marker, circle};
            },
            updatePointMarkers() {
                // Remove current markers
                for (let id in this.pointMarkers) {
                    this.markerGroup.removeLayer(this.pointMarkers[id].marker);
                    //this.map.removeLayer(this.pointMarkers[id].circle);
                }

                this.pointMarkers = {};

                // Don't create markers if no event is selected
                if (!this.event) {
                    return;
                }

                // Create markers and circles for all points unless out of time
                if (!this.outOfTime) {
                    this.event.points.forEach((point, index) => {
                        // Don't create marker if point already visited
                        if (this.visitedQuestions.indexOf(point.question._id) !== -1) {
                            return;
                        }

                        this.createPointMarker(point);
                    });
                }

                // Fit all markers into view
                this.fitBounds();
            },
            fitBounds() {
                if (Object.keys(this.pointMarkers).length) {
                    this.map.fitBounds(
                        L.featureGroup(Object.values(this.pointMarkers).map(pointMarker => pointMarker.marker)).getBounds().pad(0.5)
                    );
                }
            }
        },
        watch: {
            // Update client marker when client position changes
            clientLatLng: function () {
                this.updateClientMarker();
            },
            // Update markers when active event changes
            event: function () {
                this.updatePointMarkers();
            },
            // Update markers when team runs out of time
            outOfTime() {
                this.updatePointMarkers();
            },
            // Update view when active team changes (timeout is used to wait until visited markers are removed from the map)
            team: function () {
                setTimeout(() => {
                    this.fitBounds();
                });
            },
            // Remove visited markers from the map
            visitedQuestions: function () {
                this.visitedQuestions.forEach(id => {
                    if (id in this.pointMarkers) {
                        this.markerGroup.removeLayer(this.pointMarkers[id].marker)
                        //this.map.removeLayer(this.pointMarkers[id].circle)
                        delete this.pointMarkers[id]
                    }
                })
            }
        }
    }
</script>

<style>
    @import "~leaflet.markercluster/dist/MarkerCluster.css";
    @import "~leaflet.markercluster/dist/MarkerCluster.Default.css";

    #map {
        width: 100vw;
        height: 100vh;
    }

    .badge.badge-bordered {
        background: #fff;
        border: 2px solid;
        padding: 2px 5px;
        color: #000;
        font-weight: bold;
        border-radius: 5px;
    }

    .badge.badge-bordered.badge-index {
        border-color: #777;
    }

    .badge.badge-bordered.badge-index.leaflet-tooltip-left:before {
        border-left-color: #777;
    }

    .badge.badge-bordered.badge-index.leaflet-tooltip-right:before {
        border-right-color: #777;
    }

    .menu-button {
        padding: 5px 10px;
        background: #fff;
        cursor: pointer;
    }

    #timer {
        padding: 5px 10px;
        background: #fff;
    }
</style>


// WEBPACK FOOTER //
// map.vue?3c8e7ec0